import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  styled,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Link, useLocation } from "react-router-dom";
import PageHeading from "../utils/PageHeading";

import { findDrugByName as fetchDrugByName } from "../services/drugDataService";
import { convertDosageUnit } from "../utils/function";

// Helper to normalize dosage strings for matching across regions
// e.g., "0.5 mg (10 mg/mL)" matches "0.5 mg"
const normalizeDosage = (str) => {
  if (!str) return "";
  let s = str.toLowerCase();

  // Custom logic for Humalog mixes:
  // "Mix 25 (Mix 75/25)" -> extract the inner match and use it
  const mixMatch = s.match(/mix\s*\d+\s*\((mix\s*\d+\/\d+)\)/);
  if (mixMatch) {
     s = mixMatch[1];
  }

  // Treat IU/mL as equivalent to units/mL (e.g. Asia vs USA/Europe insulin dosages)
  s = s.replace(/\biu\b/g, "units");

  return s.replace(/\s*\([^)]*\)/g, "").trim();
};

// PriceCard was unused and removed.
const ActionButton = styled(Button)({
  backgroundColor: "#43C5B9",
  color: "white",
  borderRadius: "25px",
  textTransform: "none",
  padding: "8px 24px",
  "&:hover": {
    backgroundColor: "#43C5B9",
  },
});

// Helper function to format dosage
const formatDosage = (dosageStr) => {
  console.log("dosageStr", dosageStr);
  if (!dosageStr) return "N/A";

  const str = dosageStr.toString().toLowerCase();

  // Case 1: if contains "/", "ml", or "unit" → return as is
  if (str.includes("/") || str.includes("ml") || str.includes("unit")) {
    return dosageStr;
  }

  // Case 2: string with commas (e.g., "20,000 mcg")
  if (str.includes(",")) {
    const numericValue = parseInt(str.replace(/,/g, ""));
    if (isNaN(numericValue)) return dosageStr; // fallback

    return numericValue >= 1000
      ? `${numericValue / 1000} mg`
      : `${numericValue} mcg`;
  }

  // Case 3: plain number
  const numericValue = parseInt(str);
  if (isNaN(numericValue)) return dosageStr; // fallback if not number

  return numericValue >= 1000
    ? `${(numericValue / 1000).toFixed(0)} mg`
    : `${numericValue} mcg`;
};


// getPharmacyLogo and getPharmacyBgColor were unused and removed.

// Responsive styles
const responsiveStyles = {
  mainContainer: {
    p: { xs: 1, sm: 2, md: 3 },
    width: "100%",
    height: "auto",
    minHeight: { xs: "auto", md: "20.5625rem" },
  },
  contentBox: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: { xs: 2, md: 3 },
    pt: { xs: 1, md: 3 },
    px: { xs: 1, md: 4 },
  },
  tabsContainer: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
    gap: { xs: ".5rem", sm: ".625rem" },
    marginBottom: { xs: "1rem", sm: "1.25rem" },
  },
  tableContainer: {
    overflowX: "auto",
    width: "100%",
    WebkitOverflowScrolling: "touch",
  },
  gridContainer: {
    minWidth: { xs: "43.75rem", md: "100%" },
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr",
  },
};

// Component for region tab
const RegionTab = ({ id, text, activeTab, onClick, getBackgroundColor }) => (
  <div
    onClick={() => onClick(id)}
    style={{
      backgroundColor: getBackgroundColor(id),
      padding: "1rem",
      borderRadius: ".75rem",
      textAlign: "center",
      cursor: "pointer",
      transition: "background-color 0.3s",
    }}
    className="tab"
  >
    <div style={{ fontSize: "1.125rem" }}>{text}</div>
  </div>
);

// Helper function to extract numeric quantity from selling_unit string
const extractQuantity = (sellingUnit) => {
  if (!sellingUnit) return 1;
  const str = sellingUnit.toLowerCase();

  // Remove volume-related segments (e.g., "3 mL", "1.5 mL") so they don't affect count
  const cleanedStr = str.replace(/[\d.]+\s*ml/g, "");

  // Find all integer numbers in the cleaned string
  const numbers = cleanedStr.match(/\d+/g);
  if (!numbers || numbers.length === 0) return 1;

  // Use the largest number as the item count
  // e.g., "1 Box (5 Pens)" → [1, 5] → 5
  // e.g., "30 Tablets" → [30] → 30
  // e.g., "1 Pen (3 mL)" after cleaning → "1 Pen ()" → [1] → 1
  return Math.max(...numbers.map(n => parseInt(n, 10)));
};

// Component for pharmacy row - Updated for new data structure
const PharmacyRow = ({ product, showCoupon }) => {
  const cashPrice = product.price_cash_usd ?? 0;
  const couponPrice = product.price_with_coupon_usd ?? null;
  const quantity = extractQuantity(product.selling_unit);

  const formatUSD = (val) =>
    val != null
      ? `$${val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "N/A";

  // Common cell style
  const cellStyle = {
    padding: ".75rem",
    fontWeight: "700",
    color: "#062A3F",
    borderRight: ".0625rem dashed #00000040",
  };

  return (
    <tr style={{ borderBottom: ".0625rem dashed #00000040" }}>
      {/* Pharmacies */}
      <td style={{ padding: ".75rem", borderRight: ".0625rem dashed #00000040" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1.125rem",
            fontWeight: "bold",
            fontSize: ".875rem",
            marginLeft: ".5rem",
          }}
        >
          {product.pharmacy_name || "N/A"}
        </div>
      </td>

      {/* Manufacturer */}
      <td style={cellStyle}>{product.manufacturer || "N/A"}</td>

      {/* Drug Class */}
      <td style={cellStyle}>{product.drug_class || "N/A"}</td>

      {/* Country */}
      <td style={cellStyle}>{product.country || "N/A"}</td>

      {/* Brand */}
      <td style={cellStyle}>{product.brand_name || "Generic"}</td>

      {/* Quantity */}
      <td style={cellStyle}>
        {product.quantity_type || ""}{product.selling_unit ? ` (${product.selling_unit})` : ""}
      </td>

      {/* Cash Price */}
      <td style={cellStyle}>{formatUSD(cashPrice)}</td>

      {/* Coupon Price — USA only */}
      {showCoupon && (
        <td style={{ ...cellStyle, color: couponPrice != null ? "#2a7d4f" : "#062A3F" }}>
          {formatUSD(couponPrice)}
        </td>
      )}

      {/* Unit Price (based on cash) */}
      <td style={cellStyle}>{quantity > 0 ? formatUSD(cashPrice / quantity) : "N/A"}</td>

      {/* Dosage */}
      <td style={cellStyle}>{product.dosage_strength}</td>
    </tr>
  );
};

// Table header component
const TableHeader = ({ showCoupon }) => {
  const headers = [
    "Pharmacies",
    "Manufacturer",
    "Drug Class",
    "Country",
    "Product",
    "Quantity",
    "Cash Price",
    ...(showCoupon ? ["Coupon Price"] : []),
    "Unit Price",
    "Dosage",
  ];
  return (
  <tr
    style={{
      backgroundColor: "#f8f8f8",
      borderBottom: ".0625rem dashed #00000040",
    }}
  >
    {headers.map((header, index, arr) => (
      <th
        key={index}
        style={{
          padding: ".75rem",
          color: "#595959",
          fontWeight: "400",
          textAlign: "left",
          borderRight: index !== arr.length - 1 ? ".0625rem dashed #00000040" : "none",
        }}
      >
        {header === "Pharmacies" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: ".25rem",
              color: "#595959",
              marginLeft: ".625rem",
            }}
          >
            <span>{header}</span>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#595959",
                marginLeft: ".25rem",
              }}
            >
              <ArrowDropUpIcon
                style={{ color: "#43C5B9", fontSize: "1.25rem" }}
              />
              <ArrowDropDownIcon
                style={{
                  color: "#43C5B9",
                  fontSize: "1.25rem",
                  marginTop: "-0.75rem",
                }}
              />
            </div>
          </div>
        ) : (
          <span style={{ textAlign: "center" }}>{header}</span>
        )}
      </th>
    ))}
  </tr>
  );
};

// Helper function to convert dosage format from the JSON to display format
// const convertDosageUnit = (dosage) => {
//   return formatDosage(dosage);
// };

export default function DrugDetails() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("USA");
  const [noDataWarning, setNoDataWarning] = useState(false);
  const [drugData, setDrugData] = useState(null);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // Show disclaimer popup once per session on entry
  useEffect(() => {
    const dismissed = sessionStorage.getItem("rxpose_disclaimer_dismissed");
    if (!dismissed) {
      setDisclaimerOpen(true);
    }
  }, []);

  const handleDisclaimerClose = () => {
    sessionStorage.setItem("rxpose_disclaimer_dismissed", "1");
    setDisclaimerOpen(false);
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const medicineName = queryParams.get("medicine") || "Ozempic"; // Default to Ozempic if not specified
  const urlDosage = queryParams.get("dosage"); // Get dosage from URL parameter

  console.log("urlDosage", urlDosage);


  // Each option is { dosage, form, label } — unique combinations across all regions
  const [availableDosages, setAvailableDosages] = useState([]); // kept for region avg calc
  const [dosageForms, setDosageForms] = useState([]); // [{ dosage, form, label }]
  const [selectedDosage, setSelectedDosage] = useState(urlDosage || "");
  const [selectedForm, setSelectedForm] = useState("");

  // Helper to calculate the average price for a region
  const getRegionAverage = (regionPattern) => {
    if (!drugData || !selectedDosage) return 0;
    let total = 0;
    let count = 0;
    const region = drugData.regions.find(r => r?.region_name?.toLowerCase()?.includes(regionPattern.toLowerCase()));
    if (region) {
      region.pharmacies.forEach(p => {
        p.dosages.forEach(d => {
          if (normalizeDosage(d.dosage_strength) === normalizeDosage(selectedDosage)) {
             if (d.price_cash_usd !== undefined) {
                 total += parseFloat(d.price_cash_usd);
                 count++;
             }
          }
        });
      });
    }
    return count > 0 ? total / count : 0;
  };

  // Get dynamic background color for region tabs based on price
  const getBackgroundColor = (tab) => {
    const usaAvg = getRegionAverage("USA");
    const eurAvg = getRegionAverage("Europe");
    const asiaAvg = getRegionAverage("Asia");
    
    const validPrices = [
      { name: 'USA', price: usaAvg },
      { name: 'Europe', price: eurAvg },
      { name: 'Asia', price: asiaAvg }
    ].filter(p => p.price > 0);

    validPrices.sort((a, b) => a.price - b.price);
    
    // If this tab has no price or validPrices is empty, return default
    const thisPrice = validPrices.find(p => p.name === tab);
    if (!thisPrice) return "#f5f5f5";
    
    if (validPrices.length === 1) return "#8BD699"; // Green
    
    if (validPrices.length === 2) {
       if (validPrices[0].price === validPrices[1].price) return "#8BD699";
       return validPrices[0].name === tab ? "#8BD699" : "#FF272766"; // Min Green, Max Red
    }
    
    // length >= 3
    const minPrice = validPrices[0].price;
    const maxPrice = validPrices[validPrices.length - 1].price;
    
    if (thisPrice.price === minPrice) return "#8BD699";
    if (thisPrice.price === maxPrice) return "#FF272766";
    return "#FFE770B2"; // Yellow
  };

  // Process the medication data on component mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const foundDrug = await fetchDrugByName(medicineName);
        if (cancelled) return;

        if (!foundDrug) {
          setError("Medicine not found");
          setLoading(false);
          return;
        }

        setDrugData(foundDrug);

        // Extract unique dosage+form combinations across all regions
        const seenKeys = new Set();
        const pairs = [];
        const dosageSet = new Set();

        foundDrug.regions.forEach(region => {
          region.pharmacies.forEach(pharmacy => {
            pharmacy.dosages.forEach(dosage => {
              if (!dosage?.dosage_strength) return;
              const normD = normalizeDosage(dosage.dosage_strength);
              dosageSet.add(normD);
              const form = dosage.form || "";
              const key = `${normD}|||${form}`;
              if (!seenKeys.has(key)) {
                seenKeys.add(key);
                pairs.push({ dosage: normD, form, label: form ? `${convertDosageUnit(normD)} — ${form}` : convertDosageUnit(normD) });
              }
            });
          });
        });

        // Sort by dosage numeric value then form name
        const getNum = (str) => {
          const m = str.match(/([\d,]+(?:\.\d+)?)/);
          return m ? parseFloat(m[1].replace(/,/g, "")) : 0;
        };
        pairs.sort((a, b) => getNum(a.dosage) - getNum(b.dosage) || a.form.localeCompare(b.form));

        setDosageForms(pairs);
        setAvailableDosages(Array.from(dosageSet));

        // Default: first pair
        if (pairs.length > 0) {
          const defaultPair = pairs[0];
          setSelectedDosage(defaultPair.dosage);
          setSelectedForm(defaultPair.form);
        }

        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error("Error processing data:", err);
          setError("Failed to process medicine data");
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicineName, urlDosage]);

  // Update URL when tab or dosage changes
  useEffect(() => {
    if (selectedDosage) {
      const params = new URLSearchParams();
      params.set("medicine", medicineName);
      params.set("dosage", selectedDosage);

      // Update URL without full page refresh
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${params.toString()}`
      );
    }
  }, [selectedDosage, medicineName]);

  // Check if we have data for the selected dosage when the tab or dosage changes
  useEffect(() => {
    if (drugData && selectedDosage) {
      let hasData = false;

      // Find the region based on activeTab
      const regionNameMap = {
        "USA": "USA",
        "Europe": "Europe",
        "Asia": "Asia"
      };

      const region = drugData.regions.find(r =>
        r?.region_name?.toLowerCase()?.includes(regionNameMap[activeTab].toLowerCase())
      );

      if (region) {
        // Check if any pharmacy in this region has the selected dosage
        hasData = region.pharmacies.some(pharmacy =>
          pharmacy.dosages.some(dosage => normalizeDosage(dosage.dosage_strength) === normalizeDosage(selectedDosage))
        );
      }

      setNoDataWarning(!hasData);
    }
  }, [activeTab, selectedDosage, drugData]);

  // Function to get current pharmacies based on selected region and dosage
  const getCurrentPharmacies = () => {
    if (!drugData || !selectedDosage) return [];

    let pharmacyList = [];

    // Map activeTab to region name pattern
    const regionNameMap = {
      "USA": "USA",
      "Europe": "Europe",
      "Asia": "Asia"
    };

    // Find the region
    const region = drugData.regions.find(r =>
      r?.region_name?.toLowerCase()?.includes(regionNameMap[activeTab].toLowerCase())
    );

    if (region) {
      region.pharmacies.forEach(pharmacy => {
        pharmacy.dosages.forEach(dosage => {
          if (normalizeDosage(dosage.dosage_strength) === normalizeDosage(selectedDosage)) {
            // Create a flat pharmacy object with all needed data
            pharmacyList.push({
              pharmacy_name: pharmacy.name || pharmacy.pharmacy_name,
              url_logo: pharmacy.url_logo,
              form: dosage.form,
              dosage_strength: dosage.dosage_strength,
              selling_unit: dosage.selling_unit,
              quantity_type: dosage.quantity_type,
              price_cash_usd: dosage.price_cash_usd,
              price_with_coupon_usd: dosage.price_with_coupon_usd,
              price_with_insurance_usd: dosage.price_with_insurance_usd,
              price_local: dosage.price_local,
              currency: dosage.currency,
              url_product: dosage.url_product,
              note: dosage.note,
              brand_name: drugData.drug_metadata.brand_name,
              manufacturer: drugData.drug_metadata.manufacturer,
              drug_class: drugData.drug_metadata.drug_class,
              region_name: region.region_name,
              country: pharmacy.country
            });
          }
        });
      });
    }

    // Filter by selected form
    if (selectedForm) {
      pharmacyList = pharmacyList.filter(p => p.form === selectedForm);
    }

    // Deduplicate rows with the same pharmacy + form + selling_unit (data artifacts)
    const seen = new Set();
    pharmacyList = pharmacyList.filter(p => {
      const key = `${p.pharmacy_name}|${p.form}|${p.selling_unit}|${p.price_cash_usd}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Sort by cash price ascending
    return pharmacyList.sort((a, b) => {
      let priceA = a.price_cash_usd ?? 0;
      let priceB = b.price_cash_usd ?? 0;
      return priceA - priceB;
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <div className="about_page body_bg">
      {/* ── Data Source Disclaimer Popup ── */}
      <Dialog
        open={disclaimerOpen}
        onClose={handleDisclaimerClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
            color: "#1a202c",
            pb: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          ⚠️ About Our Data
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#2b6cb0",
              fontSize: "0.78rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Data Source &amp; Disclaimer
          </Typography>
          <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.8, mb: 2 }}>
            All drug prices displayed on this site are sourced from{" "}
            <strong>publicly available pharmacy websites</strong>, published
            price lists, and peer-reviewed research. Prices are collected
            periodically and may not reflect real-time rates. Figures shown are
            averages across multiple data points — individual pharmacy prices
            may vary.
          </Typography>
          <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.8, mb: 2 }}>
            <strong>This is a non-profit, informational website.</strong> We do
            not sell medications, earn commissions, or have any financial
            relationship with pharmacies. No transactions occur on this site.
            International price links are provided only to allow users to verify
            the data shown.
          </Typography>
          <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.8, fontSize: "0.78rem", borderTop: "1px solid #e2e8f0", pt: 1.5 }}>
            Importing prescription drugs into the U.S. is generally illegal
            under federal law. This site does not endorse or facilitate
            importation. All information is for educational purposes only.
            Consult your licensed U.S. healthcare provider for medical advice.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={handleDisclaimerClose}
            sx={{
              backgroundColor: "#43C5B9",
              color: "white",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 4,
              "&:hover": { backgroundColor: "#3ab0a5" },
            }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      <div className="about_banner_main">
        <PageHeading
          title={"Medicines"}
          subtitle={"Price Compare"}
          pageTitle="Drug Data Details"
          disclaimer="This non-profit website is for informational and educational purposes only. It compares U.S. prescription drug prices with those in other countries using publicly available data. We do not sell medications, facilitate purchases, earn commissions, or have any financial ties to pharmacies. No transactions occur on this site. Links to international websites are provided solely to allow users to verify the price data shown and do not endorse buying or importing drugs from foreign sources. Importing prescription drugs into the U.S. is generally illegal under federal law. The FDA cannot ensure the safety of foreign medications. Any importation decision is solely the user's responsibility. This site provides no medical advice. Consult your licensed U.S. healthcare provider. Use of this site is at your own risk."
        />
      </div>

      <Container
        maxWidth="xl"
        sx={{ py: { xs: 2, sm: 3, md: 4 } }}
        className="medicines_page"
      >
        {/* Medicine info and filters section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            mb: 4,
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* Medicine info */}
          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                textTransform: "capitalize",
                fontSize: { xs: "24px", sm: "28px", md: "32px" },
              }}
              className="heading_three"
            >
              {medicineName
                ? medicineName.replace(/_/g, " ")
                : "Medicine Not Found"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                maxWidth: { xs: "100%", md: "100%" },
                color: "#000000",
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              {drugData ? `${drugData.drug_metadata.generic_name} - ${drugData.drug_metadata.drug_class}` : "No description available."}
            </Typography>
          </Box>

          {/* Filters section */}
          <Box
            sx={{
              minWidth: { xs: "100%", md: "18.75rem" },
              bgcolor: "#f5f5f5",
              p: { xs: 2, sm: 3 },
              mb: { xs: 3, md: 6 },
              width: { xs: "100%", md: "50%" },
              height: "auto",
            }}
          >
            <Typography variant="subtitle1" gutterBottom className="view-text">
              Viewing
            </Typography>
            <Box
              sx={{
                width: "100%",
                padding: ".5rem 1rem",
                backgroundColor: "#43C5B9",
                color: "white",
                borderRadius: ".25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              {medicineName
                ? medicineName.charAt(0).toUpperCase() + medicineName.slice(1)
                : ""}
            </Box>

            <Typography variant="subtitle1" gutterBottom className="view-text">
              Strength &amp; Form
            </Typography>
            <Select
              fullWidth
              value={`${selectedDosage}|||${selectedForm}`}
              onChange={(e) => {
                const [dosage, form] = e.target.value.split("|||");
                setSelectedDosage(dosage);
                setSelectedForm(form);
              }}
              size="small"
              sx={{
                mb: 2,
                backgroundColor: "#43C5B9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiSelect-select": { color: "white" },
              }}
              className="select-box"
            >
              {dosageForms.map((item) => (
                <MenuItem key={`${item.dosage}|||${item.form}`} value={`${item.dosage}|||${item.form}`}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>

          </Box>
        </Box>

        {/* Main content section */}
        <Paper sx={responsiveStyles.mainContainer}>
          <Box>
            <div style={{ width: "100%" }} className="price-compare-tab">
              {/* Region tabs */}
              <Box
                sx={responsiveStyles.tabsContainer}
                className="compare-tab-items"
              >
                {[
                  { id: "USA", text: "USA Price" },
                  { id: "Europe", text: "European Price" },
                  { id: "Asia", text: "Global Price" },
                ].map((tab) => (
                  <RegionTab
                    key={tab.id}
                    id={tab.id}
                    text={tab.text}
                    activeTab={activeTab}
                    onClick={setActiveTab}
                    getBackgroundColor={getBackgroundColor}
                  />
                ))}
              </Box>

              {/* Table container */}
              <Box
                sx={responsiveStyles.tableContainer}
                className="main-tab-content"
                style={{
                  borderRadius: ".75rem",
                  boxShadow: "0 .125rem .5rem rgba(0,0,0,0.1)",
                  padding: "0",
                  overflow: "hidden",
                  backgroundColor: getBackgroundColor(activeTab),
                }}
              >
                <div className="tab-content-items">
                  {/* Warnings and alerts */}
                  {noDataWarning ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No data found for the selected dosage (
                      {formatDosage(selectedDosage)}) in {activeTab}.
                    </Alert>
                  ) : getCurrentPharmacies().length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No pharmacies available for this region and dosage.
                    </Alert>
                  ) : null}

                  {/* Pharmacies table */}
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        borderSpacing: "0",
                      }}
                      className="medicine_price"
                    >
                      <thead>
                         <TableHeader showCoupon={activeTab === "USA"} />
                       </thead>
                       <tbody className="medicine_tablebody">
                         {getCurrentPharmacies().map((product, index) => (
                           <PharmacyRow key={index} product={product} showCoupon={activeTab === "USA"} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Box>
            </div>
          </Box>
        </Paper>

        <Box sx={{ px: { xs: 2, sm: 0 } }} className="contact-content">
          <Typography
            variant="body1"
            // gutterBottom
            sx={{
              mt: 4,
              mb: 3,
              color: "#122C3A",
              fontSize: { xs: "1.5rem", sm: "1rem", md: "1.8rem" },
              textAlign: "left",
              paddingLeft: "12px",
            }}
          >
            Want to make a difference? Share these price disparities with your
            <br />
            Congressman to advocate for fair pricing.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
            }}
          >
            <ActionButton
              variant="contained"
              sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }}
              className="medi-btn"
              component={Link}
              to="/contact-congressman"
            >
              Contact Your Congressman
            </ActionButton>
          </Box>
        </Box>

      </Container>
    </div>
  );
}
